/**
 * Cart Thermometer — auto-add / auto-remove free entry products
 *
 * Logic (upgrade model — only highest earned tier is in cart):
 *   cart subtotal ≥ tier2 → add tier2 product, remove tier1 product
 *   cart subtotal ≥ tier1 → add tier1 product, remove tier2 product
 *   cart subtotal < tier1 → remove both
 *
 * "Subtotal" excludes the free entry products themselves so they
 * never count toward unlocking their own threshold.
 *
 * Config (variant IDs + cents thresholds) is read from the thermometer
 * DOM element once on init and cached in JS — so removal works even if
 * the thermometer is re-rendered or temporarily absent from the DOM.
 *
 * Removal uses item.key (variant_id:token) — the most reliable method
 * for Shopify cart items added with custom properties.
 */

window.SWWThermometer = (function () {

  // ── Cached config — populated from DOM on first successful read ───────────
  var cfg = {
    tier1VariantId : 0,
    tier2VariantId : 0,
    tier1Cents     : 0,
    tier2Cents     : 0
  };

  function loadConfig() {
    // If already cached, return immediately
    if (cfg.tier1VariantId > 0) return true;

    var el = document.querySelector('.cart-thermometer');
    if (!el) {
      console.log('[SWWThermometer] loadConfig: .cart-thermometer not in DOM yet');
      return false;
    }

    var t1v = parseInt(el.dataset.tier1Variant, 10);
    var t2v = parseInt(el.dataset.tier2Variant, 10);
    var t1c = parseInt(el.dataset.tier1Cents,   10);
    var t2c = parseInt(el.dataset.tier2Cents,   10);

    console.log('[SWWThermometer] loadConfig raw:', { t1v: t1v, t2v: t2v, t1c: t1c, t2c: t2c });

    if (t1v && t2v && t1c && t2c) {
      cfg.tier1VariantId = t1v;
      cfg.tier2VariantId = t2v;
      cfg.tier1Cents     = t1c;
      cfg.tier2Cents     = t2c;
      console.log('[SWWThermometer] Config loaded:', cfg);
      return true;
    }

    console.warn('[SWWThermometer] loadConfig: data-attributes missing or zero');
    return false;
  }

  // ── State ─────────────────────────────────────────────────────────────────
  var syncing     = false;
  var pendingSync = false;
  var syncDebounce;

  // ── Cart API helpers ──────────────────────────────────────────────────────

  function getCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  // Read giveaway status from the .cart-thermometer DOM element.
  // Returns { truck: string, live: string } where each is 'active'|'inactive'|'coolout'.
  function readGiveawayStatus() {
    var el = document.querySelector('.cart-thermometer');
    return {
      truck: el ? (el.dataset.truckGiveaway || 'inactive') : 'inactive',
      live:  el ? (el.dataset.liveGiveaway  || 'inactive') : 'inactive',
    };
  }

  function cartAdd(variantId) {
    console.log('[SWWThermometer] cartAdd variant:', variantId);

    // Build properties — always mark as free reward, also attach entry counts
    // but only inject entries for whichever giveaway is currently active.
    var addProps = { _free_reward: 'true' };
    var gs   = readGiveawayStatus();
    var swwE = window.SWWEntries ? window.SWWEntries.getForVariant(variantId) : { truck: 0, live: 0 };
    if (swwE.truck > 0 && gs.truck === 'active') {
      var _thermSpan  = document.querySelector('sww-entry-data span[data-vid="' + variantId + '"]');
      var _thermWrap  = _thermSpan ? _thermSpan.closest('sww-entry-data') : null;
      var _thermMerch = _thermWrap && _thermWrap.dataset.merch === 'true';
      var _thermBadge = _thermMerch ? (parseFloat(window.SWWBadgeMultiplier) || 1) : 1;
      var _truckVal   = swwE.truck.toLocaleString();
      if (_thermMerch && _thermBadge > 1) {
        var _mStr = (_thermBadge % 1 === 0 ? Math.round(_thermBadge) : _thermBadge) + 'x';
        _truckVal += ' Entries (' + _mStr + ')';
      }
      addProps['\uD83D\uDE9B Truck Entries'] = _truckVal;
    }

    if (swwE.live  > 0 && gs.live  === 'active') addProps['\uD83D\uDCFA Live Entries']  = swwE.live.toLocaleString();

    return fetch('/cart/add.js', {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body    : JSON.stringify({ id: variantId, quantity: 1, properties: addProps })
    }).then(function (r) {
      if (!r.ok) {
        return r.clone().json().then(function (d) {
          console.warn('[SWWThermometer] cartAdd failed:', d);
          return r;
        });
      }
      console.log('[SWWThermometer] cartAdd succeeded for variant', variantId);
      return r;
    });
  }

  // Use item KEY for removal — most reliable for items with properties.
  // Shopify's line item key is "variant_id:token" and uniquely identifies
  // the exact line regardless of cart ordering.
  function cartRemoveByKey(itemKey) {
    console.log('[SWWThermometer] cartRemove key:', itemKey);
    return fetch('/cart/change.js', {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body    : JSON.stringify({ id: itemKey, quantity: 0 })
    }).then(function (r) {
      if (!r.ok) {
        return r.clone().json().then(function (d) {
          console.warn('[SWWThermometer] cartRemove failed for key', itemKey, d);
          return r;
        });
      }
      console.log('[SWWThermometer] cartRemove succeeded for key', itemKey);
      return r;
    });
  }

  // Force a free tier item back to exactly qty 1.
  // /cart/add.js always INCREMENTS quantity — so if sync() fires twice while
  // the cart API hasn't returned the updated state yet, it can add qty 2 to the
  // same line item. This corrects that without removing the item entirely.
  function cartCapToOne(itemKey) {
    console.log('[SWWThermometer] cartCapToOne key:', itemKey);
    return fetch('/cart/change.js', {
      method  : 'POST',
      headers : { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body    : JSON.stringify({ id: itemKey, quantity: 1 })
    }).then(function (r) {
      if (!r.ok) {
        return r.clone().json().then(function (d) {
          console.warn('[SWWThermometer] cartCapToOne failed for key', itemKey, d);
          return r;
        });
      }
      console.log('[SWWThermometer] cartCapToOne succeeded for key', itemKey);
      return r;
    });
  }

  // ── Section re-render helpers ─────────────────────────────────────────────

  async function refreshCartPage() {
    try {
      var mainCartEl = document.getElementById('main-cart-items');
      var footerEl   = document.getElementById('main-cart-footer');
      if (!mainCartEl) return;

      // Use the same section IDs Dawn uses (from data-id attributes).
      var sectionId       = mainCartEl.dataset.id;
      var footerSectionId = footerEl ? footerEl.dataset.id : null;

      var sectionsToFetch = [sectionId, 'cart-icon-bubble', 'cart-notification-button'];
      if (footerSectionId) sectionsToFetch.push(footerSectionId);

      var res = await fetch(
        window.location.pathname + '?sections=' + sectionsToFetch.join(',')
      );
      if (!res.ok) return;
      var sections = await res.json();

      // ── Cart items — same target as Dawn: #main-cart-items .js-contents ──
      if (sections[sectionId]) {
        var cartDoc = new DOMParser().parseFromString(sections[sectionId], 'text/html');

        // Update the items table
        var newContents = cartDoc.querySelector('#main-cart-items .js-contents');
        var oldContents = mainCartEl.querySelector('.js-contents');
        if (newContents && oldContents) {
          oldContents.innerHTML = newContents.innerHTML;
        }

        // Sync is-empty on the <cart-items> wrapper element
        var newCartEl = cartDoc.querySelector('cart-items');
        var oldCartEl = document.querySelector('cart-items');
        if (newCartEl && oldCartEl) {
          oldCartEl.classList.toggle('is-empty', newCartEl.classList.contains('is-empty'));
        }

        // Update thermometer (lives inside <cart-items> but outside #main-cart-items)
        var newThermo = cartDoc.querySelector('.cart-thermometer');
        var oldThermo = document.querySelector('cart-items .cart-thermometer');
        if (newThermo) {
          if (oldThermo) {
            oldThermo.outerHTML = newThermo.outerHTML;
          } else {
            // Insert before #main-cart-items's parent form, or before the form
            var formEl = mainCartEl.closest('form') || mainCartEl;
            if (formEl.parentNode) {
              formEl.parentNode.insertBefore(newThermo.cloneNode(true), formEl);
            }
          }
        } else if (oldThermo) {
          oldThermo.remove();
        }
      }

      // ── Cart footer / totals — same target: #main-cart-footer .js-contents ──
      if (footerSectionId && sections[footerSectionId] && footerEl) {
        var footerDoc = new DOMParser().parseFromString(sections[footerSectionId], 'text/html');
        var newFooterContents = footerDoc.querySelector('.js-contents');
        var oldFooterContents = footerEl.querySelector('.js-contents');
        if (newFooterContents && oldFooterContents) {
          oldFooterContents.innerHTML = newFooterContents.innerHTML;
        }
      }

      refreshBadges(sections);
    } catch (e) {
      console.warn('[SWWThermometer] refreshCartPage error:', e);
    }
  }

  async function refreshCartDrawer() {
    try {
      var res = await fetch('/?sections=cart-drawer,cart-notification-button,cart-icon-bubble');
      if (!res.ok) return;
      var sections = await res.json();

      if (!sections['cart-drawer']) return;

      var tmp = document.createElement('div');
      tmp.innerHTML = sections['cart-drawer'];

      // Sync is-empty on <cart-drawer> wrapper
      var newCartEl = tmp.querySelector('cart-drawer');
      var oldCartEl = document.querySelector('cart-drawer');
      if (newCartEl && oldCartEl) {
        oldCartEl.classList.toggle('is-empty', newCartEl.classList.contains('is-empty'));
      }

      // Replace the full .drawer__inner — this includes header, thermometer,
      // cart-drawer-items and footer in one shot (same approach as Dawn's
      // CartDrawerItems.updateQuantity uses for selector '.drawer__inner')
      var newInner = tmp.querySelector('.drawer__inner');
      var oldInner = document.querySelector('#CartDrawer .drawer__inner');
      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
        return refreshBadges(sections);
      }

      // Fallback: update sub-elements individually
      var newItems = tmp.querySelector('cart-drawer-items');
      var oldItems = document.querySelector('cart-drawer-items');
      if (newItems && oldItems) {
        oldItems.classList.toggle('is-empty', newItems.classList.contains('is-empty'));
        oldItems.innerHTML = newItems.innerHTML;
      }

      var newHeading = tmp.querySelector('.drawer__heading');
      var oldHeading = document.querySelector('#CartDrawer .drawer__heading');
      if (newHeading && oldHeading) oldHeading.innerHTML = newHeading.innerHTML;

      var newFooter = tmp.querySelector('.drawer__footer');
      var oldFooter = document.querySelector('#CartDrawer .drawer__footer');
      if (newFooter && oldFooter) oldFooter.innerHTML = newFooter.innerHTML;

      // Thermometer — outside cart-drawer-items
      var newThermo = tmp.querySelector('.cart-thermometer');
      var oldThermo = document.querySelector('#CartDrawer .cart-thermometer');
      if (newThermo) {
        if (oldThermo) {
          oldThermo.outerHTML = newThermo.outerHTML;
        } else {
          var drawerItemsEl = document.querySelector('cart-drawer-items');
          if (drawerItemsEl && drawerItemsEl.parentNode) {
            drawerItemsEl.parentNode.insertBefore(newThermo.cloneNode(true), drawerItemsEl);
          }
        }
      } else if (oldThermo) {
        oldThermo.remove();
      }

      refreshBadges(sections);
    } catch (e) {
      console.warn('[SWWThermometer] refreshCartDrawer error:', e);
    }
  }

  function refreshBadges(sections) {
    // ── Cart count bubble in the header ──────────────────────────────────────
    // The header renders <a id="cart-icon-bubble"> containing .cart-count-bubble.
    // The Section Rendering API returns the section HTML for 'cart-icon-bubble'
    // which also contains a fresh .cart-count-bubble — we swap just that element.
    if (sections['cart-icon-bubble']) {
      var bubbleHost = document.getElementById('cart-icon-bubble');
      if (bubbleHost) {
        var tmp = document.createElement('div');
        tmp.innerHTML = sections['cart-icon-bubble'];
        var newBubble = tmp.querySelector('.cart-count-bubble');
        var oldBubble = bubbleHost.querySelector('.cart-count-bubble');
        if (newBubble && oldBubble) {
          oldBubble.outerHTML = newBubble.outerHTML;
        } else if (newBubble) {
          bubbleHost.appendChild(newBubble);
        }
      }
    }
  }

  // ── Core sync ─────────────────────────────────────────────────────────────

  async function sync() {
    if (syncing) {
      pendingSync = true;
      return;
    }

    syncing = true;

    try {
      var cart = await getCart();

      // ── Hide/show thermometer based on paid subtotal ─────────────────────
      // Runs BEFORE the config check so it works even when tier products are
      // not configured in theme settings (loadConfig would return false and
      // the rest of sync would be skipped, but visibility must still update).
      var thermoEl = document.querySelector('.cart-thermometer');
      if (thermoEl) {
        var paidCheck = 0;
        cart.items.forEach(function (item) {
          // Exclude known free-tier items from the paid check when config loaded
          var isTierItem = cfg.tier1VariantId && (
            item.variant_id === cfg.tier1VariantId ||
            item.variant_id === cfg.tier2VariantId
          );
          if (!isTierItem) paidCheck += item.final_line_price;
        });
        thermoEl.style.display = paidCheck > 0 ? '' : 'none';
      }

      // ── The add/remove logic requires tier config ────────────────────────
      if (!loadConfig()) {
        console.log('[SWWThermometer] sync skipped for add/remove logic — config not loaded yet');
        return;
      }

      console.log('[SWWThermometer] sync start | cfg:', cfg);

      // ── Scan cart items ──────────────────────────────────────────────────
      // Track KEYS and QUANTITIES of each free tier in the cart.
      var keys1    = []; // keys of tier1 free items
      var keys2    = []; // keys of tier2 free items
      var qtys1    = {}; // itemKey → quantity for tier1 items
      var qtys2    = {}; // itemKey → quantity for tier2 items
      var subtotal = 0;

      cart.items.forEach(function (item) {
        if (item.variant_id === cfg.tier1VariantId) {
          keys1.push(item.key);
          qtys1[item.key] = item.quantity;
        } else if (item.variant_id === cfg.tier2VariantId) {
          keys2.push(item.key);
          qtys2[item.key] = item.quantity;
        } else {
          subtotal += item.final_line_price;
        }
      });

      var has1   = keys1.length > 0;
      var has2   = keys2.length > 0;
      var atTier2 = subtotal >= cfg.tier2Cents;
      var atTier1 = subtotal >= cfg.tier1Cents;
      var changed = false;

      // Read live giveaway status — free products award entries so we only
      // auto-add them when at least one giveaway is running.
      var gs = readGiveawayStatus();
      var giveawayRunning = (gs.truck === 'active' || gs.live === 'active');

      console.log(
        '[SWWThermometer] sync state | subtotal:', subtotal,
        '| t1Cents:', cfg.tier1Cents, '| t2Cents:', cfg.tier2Cents,
        '| atTier1:', atTier1, '| atTier2:', atTier2,
        '| keys1:', keys1, '| keys2:', keys2,
        '| giveawayRunning:', giveawayRunning
      );

      // ── Safety net: enforce max 1 of each tier item ─────────────────────
      // Two cases handled:
      //
      //  A) Duplicate line items (same variant, different properties):
      //     Shopify creates separate lines when properties differ. Remove all
      //     but the newest (last-added) key.
      //
      //  B) Single line item with qty > 1:
      //     /cart/add.js INCREMENTS quantity on an existing line. If sync()
      //     fires while the cart state hasn't updated yet, it calls cartAdd()
      //     twice on the same line → qty 2. Force it back to 1.
      while (keys1.length > 1) {
        await cartRemoveByKey(keys1.shift()); // remove oldest, keep newest
        changed = true;
      }
      if (keys1.length === 1 && qtys1[keys1[0]] > 1) {
        await cartCapToOne(keys1[0]);
        changed = true;
      }
      while (keys2.length > 1) {
        await cartRemoveByKey(keys2.shift()); // remove oldest, keep newest
        changed = true;
      }
      if (keys2.length === 1 && qtys2[keys2[0]] > 1) {
        await cartCapToOne(keys2[0]);
        changed = true;
      }
      // Re-evaluate has1 / has2 after cleanup
      has1 = keys1.length > 0;
      has2 = keys2.length > 0;

      // ── Apply threshold logic ────────────────────────────────────────────

      if (!giveawayRunning) {
        // Giveaway is not active — remove ALL free items regardless of threshold.
        // Never add new ones until a giveaway starts.
        var allFreeKeys = keys1.concat(keys2);
        if (allFreeKeys.length > 0) {
          console.log('[SWWThermometer] giveaway inactive — removing free keys:', allFreeKeys);
          for (var i = 0; i < allFreeKeys.length; i++) {
            await cartRemoveByKey(allFreeKeys[i]); changed = true;
          }
        }

      } else if (atTier2) {
        // Tier-2 earned — add tier2 if missing, remove all tier1
        if (!has2) { await cartAdd(cfg.tier2VariantId); changed = true; }
        for (var i = 0; i < keys1.length; i++) {
          await cartRemoveByKey(keys1[i]); changed = true;
        }

      } else if (atTier1) {
        // Tier-1 earned — add tier1 if missing, remove all tier2
        if (!has1) { await cartAdd(cfg.tier1VariantId); changed = true; }
        for (var i = 0; i < keys2.length; i++) {
          await cartRemoveByKey(keys2[i]); changed = true;
        }

      } else {
        // Below all thresholds — remove ALL free items (both tiers)
        var allFreeKeys = keys1.concat(keys2);
        console.log('[SWWThermometer] below threshold — removing free keys:', allFreeKeys);
        for (var i = 0; i < allFreeKeys.length; i++) {
          await cartRemoveByKey(allFreeKeys[i]); changed = true;
        }
      }

      if (changed) {
        console.log('[SWWThermometer] cart changed — refreshing UI');
        var isCartPage = !!document.getElementById('main-cart-items');
        if (isCartPage) {
          await refreshCartPage();
        } else {
          await refreshCartDrawer();
        }
      } else {
        console.log('[SWWThermometer] no change needed');
      }

    } catch (e) {
      console.warn('[SWWThermometer] sync error:', e);
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        setTimeout(sync, 400);
      }
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    // Attempt to load config immediately
    loadConfig();

    // Run initial sync (in case items already in cart on page load)
    sync();

    // Subscribe to Dawn's cart-update pubsub event.
    // Fires after: quantity ± buttons, remove button, product-form add, quick-entries add.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // Debounce: wait for Dawn to finish its own section re-render first,
        // then re-attempt config load in case thermometer just appeared.
        clearTimeout(syncDebounce);
        syncDebounce = setTimeout(function () {
          // Re-read config if it wasn't cached yet (e.g. cart was empty on init)
          if (cfg.tier1VariantId === 0) loadConfig();
          sync();
        }, 600);
      });
      console.log('[SWWThermometer] subscribed to PUB_SUB_EVENTS.cartUpdate');
    } else {
      console.warn('[SWWThermometer] subscribe / PUB_SUB_EVENTS not available — falling back to cart.js hook only');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { sync: sync, loadConfig: loadConfig, cfg: cfg };

})();
